import { Actions, Async, FSpread } from '@client/new/helpers/types';
import { useEffect, useState } from 'react';
import { isEmpty, omit } from 'lodash';
import { useHomeModals, ModalType } from './hooks/useHomeModals';
import { Influencer } from '@playbook-technologies/common-enums';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  Feedback,
  getChecklistItems,
  getLinks,
  toFeedbackData,
  CreatorLinksType,
  getHomeCta,
  getOTPPrograms,
  getSubscriptionPrograms,
  getAfterDesc,
  externalUrls,
} from './mappers';
import { ButtonColor } from '@design-system/components/button/button.css';
import { browserHistory } from 'react-router';
import { PATHS } from '@client/routes';
import { Collections, CollectionType, SubscriptionType } from './home.domain';
import toast from 'react-hot-toast';
import { Offer, useHomeModel } from './home.model';
import { trackEvent, usePublishedProducts } from '@services/tracking';
import { ExclusivePriceSetupProps } from '@client/new/common/selfServeExclusive/exclusivePriceSetup';
import { useExclusiveViewModel } from '@client/new/common/selfServeExclusive/exclusive.viewModel';
import { toExclusiveSettingsView } from '@client/new/common/selfServeExclusive/mappers';
import { _noop } from '@client/new/helpers/general';
import { getDmEnabled } from '@client/new/common/features.helpers';

export type HomeVM = {
  dispatch: HomeVMVDispatcher;
  headline: {
    title: string;
    link?: string;
    cta?: Array<{
      icon: IconProp;
      onClick: () => void;
      content?: string;
      className?: string;
      disabled?: boolean;
      color?: ButtonColor;
    }>;
  };
  isOnboarding: boolean;
  isLive: boolean;
  influencerId: number;
  uid: string;
  offerModal: {
    isOpen: boolean;
    onClose: () => void;
    collection: InitialCollection;
    setName: (value: string) => void;
    setType: (type: CollectionType) => void;
    createOffer: () => void;
    submitting: boolean;
    videoPlayed: (url: string) => void;
    offerTypeSelected: (type: CollectionType) => void;
    subscriptionTypeSelected: (type: SubscriptionType) => void;
    exclusive: ExclusivePriceSetupProps;
    savePricing: (callback?: () => void) => void;
    errors?: string[];
  };
  educationCloseConfirmation: {
    isOpen: boolean;
    onClose: () => void;
    confirmClose: () => void;
  };
  goLiveModal: {
    isOpen: boolean;
    onClose: () => void;
    version: CollectionType | 'both';
    goLive: () => void;
    inProgress: boolean;
  };
  liveSuccessModal: {
    isOpen: boolean;
    onClose: () => void;
    version: CollectionType | 'both';
    influencerId: number;
  };
  onboarding: {
    checklist: Array<{
      title: string;
      done: boolean;
      to?: string;
      onClick?: () => void;
      icon: IconProp;
    }>;
    progres: number;
    completed: number;
  };
  feedback: {
    show: boolean;
    data: Feedback;
    dmEnabled: boolean;
  };
  linksModal: {
    showLinks: boolean;
    links: CreatorLinksType;
    afterDesc: React.ReactNode;
    isOpen: boolean;
  };
  openedModal: ModalType;
  // track: (type: HomeScreenEvent['type']) => void;
};

export type PayloadMap = {
  closeModal: null;
  setupOffer: null;
  setCollectionName: string;
  setCollectionType: CollectionType;
  openGoLiveModal: null;
  createOffer: null;
  goLive: null;
  backToEducation: null;
  confirmCloseEducation: null;
  openConfirmEducationClose: null;
  selectSubscriptionType: SubscriptionType;
  videoPlayed: string;
  offerTypeSelectedEvent: CollectionType;
  subscriptionTypeSelectedEvent: SubscriptionType;
  openLinksModal: null;
  openModal: ModalType;
  modalOpened: ModalType;
  challengeJoined: null;
  communityJoined: null;
};

export type HomeVMActions = Actions<PayloadMap>;
export type HomeVMVDispatcher = FSpread<PayloadMap>;

export interface InitialCollection extends Omit<Offer, 'name'> {
  name?: string;
}

const initialCollection: InitialCollection = {
  name: undefined,
  type: 'otp',
  subscriptionType: undefined,
};

export const useHomeViewModel = (p: {
  influencerId?: string;
}): Async<HomeVM> => {
  const [initCollection, setInitCollection] =
    useState<InitialCollection>(initialCollection);

  const asyncExclusiveModel = useExclusiveViewModel(p);
  const asyncModel = useHomeModel(p);
  const publishedProducts = usePublishedProducts(p);

  const asyncModals = useHomeModals({
    model: asyncModel,
  });

  // Track when creator goes live
  useEffect(() => {
    if (publishedProducts.type !== 'fetched') return;
    if (asyncModel.type !== 'fetched') return;

    const creatorWentLive = !asyncModel.value.creatorWentLive;
    if (creatorWentLive) {
      trackEvent({
        type: 'Creator Is Live',
        payload: {
          'Product Type': publishedProducts.value.publishedProductType,
          'Collection Type': publishedProducts.value.publishedCollectionType,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncModel.type, publishedProducts]);

  if (asyncModel.type !== 'fetched') return asyncModel;
  if (publishedProducts.type !== 'fetched') return publishedProducts;
  if (asyncExclusiveModel.type !== 'fetched') return asyncExclusiveModel;
  if (asyncModals.type !== 'fetched') return asyncModals;

  const model = asyncModel.value;
  const modals = asyncModals.value;
  const exclusiveModel = asyncExclusiveModel.value;
  const dispatch: HomeVMVDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as HomeVMActions;
    switch (action.type) {
      case 'challengeJoined': {
        trackEvent({
          type: 'Onboarding Tile Tapped',
          payload: {
            Title: 'Join the Black Friday Launch Challenge',
            Source: isLive ? 'Post-Launch' : 'Pre-Launch',
          },
        });
        window.open(externalUrls['blackFridayChallenge'], '_blank');
        return null;
      }
      case 'communityJoined': {
        trackEvent({
          type: 'Onboarding Tile Tapped',
          payload: {
            Title: 'Join our FREE Creator Community',
            Source: isLive ? 'Post-Launch' : 'Pre-Launch',
          },
        });
        window.open(externalUrls['community'], '_blank');
        return null;
      }
      case 'modalOpened':
        if (action.payload === 'QrCode')
          trackEvent({
            type: 'Creator app downloaded',
          });

        return null;
      case 'openModal':
        modals.openModal(action.payload);
        return null;
      case 'openLinksModal':
        modals.openModal('Links');
        return null;
      case 'closeModal':
        modals.closeModal();
        return null;
      case 'setupOffer':
        setupOffer();
        return null;
      case 'openGoLiveModal':
        trackEvent({
          type: 'Go Live Tapped',
          payload: {
            'Product Type': publishedProducts.value.publishedProductType,
            'Collection Type': publishedProducts.value.publishedCollectionType,
          },
        });
        modals.openModal('GoLive');
        return null;
      case 'setCollectionName':
        setInitCollection((prev) => ({ ...prev, name: action.payload }));
        return null;
      case 'setCollectionType':
        setInitCollection((prev) => ({ ...prev, type: action.payload }));
        return null;
      case 'createOffer':
        createOffer();
        return null;
      case 'goLive':
        model.dispatch('goLive');
        return null;
      case 'confirmCloseEducation':
        modals.confirmCloseEducation();
        setInitCollection(initialCollection);
        return null;
      case 'backToEducation':
        modals.backToEducation();
        return null;
      case 'openConfirmEducationClose':
        modals.openConfirmEducationClose();
        return null;
      case 'selectSubscriptionType':
        setInitCollection((prev) => ({
          ...prev,
          subscriptionType: action.payload,
        }));
        return null;
      case 'offerTypeSelectedEvent':
        trackEvent({
          type:
            action.payload === 'otp'
              ? 'OTP has been selected within our Go Live Modal Flow'
              : 'Subscription has been selected within our Go Live Modal Flow',
          payload: {},
        });
        return null;
      case 'subscriptionTypeSelectedEvent':
        trackEvent({
          type:
            action.payload === 'ppc'
              ? 'PPC has been selected within our Go Live Modal Flow'
              : 'Platform has been selected within our Go Live Modal Flow',
          payload: {},
        });
        return null;
      case 'videoPlayed':
        trackEvent({
          type: 'Education Video Watched',
          payload: {
            'Video Watched': action.payload,
          },
        });
        return null;
    }
  };

  const influencer = model.data?.creator;
  const isLive = model.data?.creator?.mode === Influencer.Mode.Live.value;
  const onboardingList = omit(model.data?.onboardingListData, '__typename');
  const customURL = influencer?.customURL || undefined;
  const profileDeepLink = influencer?.profileDeepLink || undefined;
  const otpPrograms: Collections = getOTPPrograms(model.data.collections);
  const subscriptionPrograms: Collections = getSubscriptionPrograms(
    model.data.collections,
  );
  const isCreatorExclusive = model.data.creator?.visibility === 'exclusive';

  const linksType =
    otpPrograms.length > 0 && subscriptionPrograms.length > 0
      ? 'both'
      : otpPrograms.length > 0
      ? 'otp'
      : 'subscription';

  const hasLinks = otpPrograms.length > 0 || subscriptionPrograms.length > 0;

  const dmEnabled = getDmEnabled(influencer?.features);

  const createOffer = async () => {
    if (!initCollection.name) {
      toast.error('Collection name is missing');
      return;
    }
    model.dispatch('createOffer', initCollection as Offer);
  };

  const setupOffer = () => {
    trackEvent({ type: 'Set Your Offering Tapped' });
    if (isEmpty(model.data.collections)) {
      modals.openModal('Offer');
      return;
    }
    browserHistory.push(
      PATHS.PROGRAM_EDIT(model.data.collections[0].id, model.influencerId),
    );
  };

  const checklist = getChecklistItems({
    data: onboardingList,
    creatorId: model.influencerId,
    email: influencer?.email || undefined,
    isLive,
    dispatch,
  });
  const checklistCompleted = checklist.filter((i) => i.done).length;
  const checklistProgress = Math.ceil((checklistCompleted * 100) / 3);

  const canGoLive = Object.values(onboardingList).every((i) => i === true);
  const goLiveVersion =
    !isEmpty(otpPrograms) && !isEmpty(subscriptionPrograms)
      ? 'both'
      : !isEmpty(subscriptionPrograms)
      ? 'subscription'
      : 'otp';

  return {
    type: 'fetched',
    value: {
      openedModal: modals.modalType,
      dispatch,
      headline: {
        title: `Welcome ${influencer?.firstName || ''} 👋`,
        link: isLive
          ? 'https://docs.google.com/document/d/1ufQNOy-6-v5VfnPDrDEVJqIPCV_ILOtPY-wGM2ZakbE/preview'
          : undefined,
        cta: getHomeCta({ isLive, dispatch, canGoLive }),
      },
      // track: trackMixPanel(publishedProducts.value),
      isOnboarding:
        model.data?.creator?.mode === Influencer.Mode.Onboarding.value,
      isLive,
      influencerId: model.influencerId,
      uid: model.uid,
      onboarding: {
        checklist,
        progres: checklistProgress,
        completed: checklistCompleted,
      },
      linksModal: {
        showLinks: hasLinks && isLive,
        isOpen: modals.isModalOpen('Links'),
        links: getLinks(
          linksType,
          isCreatorExclusive,
          customURL,
          profileDeepLink,
        ),
        afterDesc: getAfterDesc(linksType, model.influencerId),
      },
      offerModal: {
        isOpen: modals.isModalOpen('Offer'),
        onClose: () => dispatch('openConfirmEducationClose'),
        collection: initCollection,
        setName: (value: string) => dispatch('setCollectionName', value),
        setType: (type: CollectionType) => dispatch('setCollectionType', type),
        createOffer: () => dispatch('createOffer'),
        submitting: model.submittingOffer,
        videoPlayed: (url: string) => dispatch('videoPlayed', url),
        offerTypeSelected: (type: CollectionType) =>
          dispatch('offerTypeSelectedEvent', type),
        subscriptionTypeSelected: (type: SubscriptionType) =>
          dispatch('subscriptionTypeSelectedEvent', type),
        exclusive: toExclusiveSettingsView(exclusiveModel),
        savePricing: (callback?: () => void) =>
          exclusiveModel.dispatch('saveExclusive', callback || _noop),
        errors: Object.values(exclusiveModel.errors)
          .map((error) => error.message)
          .filter(Boolean) as string[],
      },
      educationCloseConfirmation: {
        isOpen: modals.confirmEducationClose,
        onClose: () => dispatch('backToEducation'),
        confirmClose: () => dispatch('confirmCloseEducation'),
      },
      goLiveModal: {
        isOpen: modals.isModalOpen('GoLive'),
        onClose: () => dispatch('closeModal'),
        version: goLiveVersion,
        goLive: () => dispatch('goLive'),
        inProgress: model.goLiveProgress,
      },
      liveSuccessModal: {
        isOpen: modals.isModalOpen('LiveSuccess'),
        onClose: () => dispatch('closeModal'),
        version: goLiveVersion,
        influencerId: model.influencerId,
      },
      feedback: {
        show: isLive,
        data: toFeedbackData(model.data.searchComments),
        dmEnabled,
      },
    },
  };
};
